// src/utils/calcModifyDatesPreview.ts
import moment from "moment";
import { calcBookingBreakdown } from "utils/calcBookingBreakdown";

type TripLike = any;

export type ModifyDatesPreviewInput = {
  trip: TripLike;
  bookingId: string;
  dateRange: [string, string] | null | undefined;
  applyToAll: boolean;

  // optional flags from UI
  hasUserChangedDates: boolean;
  isDatesInvalid: boolean;

  // if we want to hide add-ons/coupon/discount lines on Modify Dates page
  roomsOnly?: boolean;
};

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

export const safeISO = (v: any) => {
  if (!v) return "";
  return v instanceof Date ? v.toISOString() : String(v);
};

export const calcNightsUTC = (checkInISO: string, checkOutISO: string) => {
  if (!checkInISO || !checkOutISO) return 0;

  const inDay = moment.utc(checkInISO).startOf("day");
  const outDay = moment.utc(checkOutISO).startOf("day");

  if (!inDay.isValid() || !outDay.isValid()) return 0;

  return Math.max(0, outDay.diff(inDay, "days"));
};

type ApplyEditedDatesArgs = {
  trip: TripLike;
  bookingId: string;
  dateRange: [string, string];
  applyToAll: boolean;
};

type PreviewLine = {
    key: string;
    title: string;
    amount: number;
    red?: boolean;
    isModified?: boolean;
  };

/**
 * Apply edited dates into a cloned trip to reuse calcBookingBreakdown.
 * IMPORTANT: This is only a preview.
 * We keep existing pricePerNight and recompute numberOfNights for current roomBookings.
 */
export const applyEditedDatesToTrip = ({
  trip,
  bookingId,
  dateRange,
  applyToAll,
}: ApplyEditedDatesArgs) => {
  if (!trip) return trip;

  const t = clone(trip);
  const [newCheckInISO, newCheckOutISO] = dateRange;

  const applyForBooking = (b: any) => {
    b.checkInDate = newCheckInISO;
    b.checkOutDate = newCheckOutISO;

    const nights = calcNightsUTC(newCheckInISO, newCheckOutISO);

    // Update roomBookings nights so calcBookingBreakdown prices rooms correctly
    if (Array.isArray(b?.roomBookings)) {
      for (let i = 0; i < b.roomBookings.length; i++) {
        const rb = b.roomBookings[i];
        if (!rb) continue;

        // Keep backend "deleted" logic intact: calcBookingBreakdown already filters
        rb.numberOfNights = nights;

        // Force recompute from pricePerNight * nights (safer for preview)
        // because totalPrice from backend might be stale for new dates.
        rb.totalPrice = 0;
      }
    }
  };

  for (const b of t?.bookings || []) {
    const shouldApply = applyToAll ? true : b?.bookingId === bookingId;
    if (!shouldApply) continue;
    applyForBooking(b);
  }

  return t;
};

export const calcModifyDatesPreview = (input: ModifyDatesPreviewInput) => {
  const {
    trip,
    bookingId,
    dateRange,
    applyToAll,
    hasUserChangedDates,
    isDatesInvalid,
    roomsOnly = true,
  } = input;

  if (!trip) return null;
  if (!dateRange?.[0] || !dateRange?.[1]) return null;
  if (!hasUserChangedDates) return null;
  if (isDatesInvalid) return null;
  if (!bookingId) return null;

  const nextTrip = applyEditedDatesToTrip({
    trip,
    bookingId,
    dateRange: [String(dateRange[0]), String(dateRange[1])],
    applyToAll,
  });

  const breakdown = calcBookingBreakdown(nextTrip);

  const modifiedBookingIds = new Set<string>();

  if (applyToAll) {
    for (const b of trip?.bookings || []) {
      if (b?.bookingId) modifiedBookingIds.add(String(b.bookingId));
    }
  } else if (bookingId) {
    modifiedBookingIds.add(String(bookingId));
  }

  if (!roomsOnly) return breakdown;

  // roomsOnly: keep only booking lines + MAT + HST
  const filtered: PreviewLine[] = (breakdown.lines as any[]).filter((l) => {
    const k = String(l?.key || "");
    return k.startsWith("booking_") || k === "mat" || k === "hst";
  }).map((l) => {
    const k = String(l?.key || "");
    if (k.startsWith("booking_")) {
      const id = k.replace(/^booking_/, "");
      return { ...l, isModified: modifiedBookingIds.has(id) };
    }
    return l;
  });




  return {
    ...breakdown,
    lines: filtered,
    // keep totals from calcBookingBreakdown for consistency
  };
};